import { useEffect, useRef } from 'react'

import classNames from 'classnames'
import { t } from 'i18next'
import { useFormContext, Controller } from 'react-hook-form'
import {
  components as rsComponents,
  OptionProps,
  SingleValueProps,
} from 'react-select'

import { DESCRIPTION_MAX_LENGTH } from './constants'
import { FieldErrorMessage } from './FieldErrorMessage'
import { LogTimeFormBillingSection } from './LogTimeFormBillingSection'
import { LogTimeFormDatePicker } from './LogTimeFormDatePicker'
import { TaskButton } from './TaskButton'
import { SelectOption, LogTimeFormProps } from './types'
import { UserMultiSelect } from './UserMultiSelect'
import { ActivityType } from '../TimeTrackingOverviewWidget/const'
import { StringText } from '@/Atomic/Atoms/StringText'
import { Input } from '@/Atomic/Molecules/Inputs/Input'
import { Select } from '@/Atomic/Molecules/Inputs/Select'
import { Textarea } from '@/Atomic/Molecules/Inputs/Textarea'
import { Tooltip } from '@/Atomic/Molecules/Tooltip'
import {
  Form,
  FormBody,
  FormFields,
  FormGroup,
  FormHeader,
} from '@/Atomic/Templates/Form'

import './LogTimeForm.scss'

const LABEL_TOOLTIP_ID = 'log-time-select-label-tooltip'

const TruncatedLabel = ({
  label,
  children,
}: {
  label: string
  children: React.ReactNode
}) => {
  const ref = useRef<HTMLSpanElement>(null)

  const syncTooltip = () => {
    const el = ref.current
    if (!el) return
    const truncated = el.scrollWidth > el.clientWidth
    el.setAttribute('data-tooltip-content', truncated ? label : '')
  }

  return (
    <span
      ref={ref}
      className="d-block text-truncate"
      data-tooltip-id={LABEL_TOOLTIP_ID}
      data-tooltip-content=""
      onPointerOver={syncTooltip}
      onMouseMove={syncTooltip}
    >
      {children}
    </span>
  )
}

const TitleOption = (props: OptionProps) => (
  <rsComponents.Option {...props}>
    <TruncatedLabel label={(props.data as SelectOption).label}>
      {props.children}
    </TruncatedLabel>
  </rsComponents.Option>
)

const TitleSingleValue = (props: SingleValueProps) => (
  <rsComponents.SingleValue {...props}>
    <TruncatedLabel label={(props.data as SelectOption).label}>
      {props.children}
    </TruncatedLabel>
  </rsComponents.SingleValue>
)

const tooltipComponents = {
  Option: TitleOption,
  SingleValue: TitleSingleValue,
}

const clampMenuClassNames = {
  menuPortal: () => 'log-time-select-portal',
  container: () => 'log-time-select-container',
}

export const LogTimeForm = ({
  customers,
  typesOfActivity,
  productsTypes,
  predefinedTasks,
  orders,
  workPackages,
  users,
  isEditMode,
  currentUserId,
  isLeader = false,
  triggerValidation,
}: LogTimeFormProps) => {
  const {
    getValues,
    setValue,
    getFieldState,
    control,
    watch,
    formState: { errors, isSubmitting },
  } = useFormContext()
  const formValues = getValues()
  const defaultDate = formValues.date

  const isSupportType = watch('typeOfActivity') === ActivityType.SUPPORT
  const descriptionErrors = [errors.description].filter((error) => !!error)

  const descriptionLength = (watch('description') || '').length

  useEffect(() => {
    if (errors.description) {
      document
        .getElementById('description')
        ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
    }
  }, [errors.description])

  return (
    <div>
      <Form>
        <fieldset disabled={isSubmitting} className="log-time-form-fieldset">
          <FormBody className="w-100 gap-3">
            <FormGroup>
              <FormFields className="gap-1">
                <Controller
                  name="users"
                  control={control}
                  render={({ field, fieldState: { error } }) => (
                    <>
                      <UserMultiSelect
                        options={users}
                        value={field.value}
                        onChange={field.onChange}
                        label={t('timeTracking.form.user')}
                        currentUserId={currentUserId}
                        isDisabled={isEditMode}
                        hasError={!!error}
                        lockCurrentUser={!isLeader}
                      />
                      <FieldErrorMessage error={error} />
                    </>
                  )}
                />
              </FormFields>
            </FormGroup>

            <FormGroup>
              <LogTimeFormDatePicker
                defaultValue={defaultDate}
                onChange={(value) => setValue('date', value)}
              />
            </FormGroup>

            <FormHeader>
              <StringText variant="title" className="lh-base fs-6">
                {t('timeTracking.form.project')}
              </StringText>
            </FormHeader>

            <FormGroup>
              <FormFields className="gap-1">
                <Controller
                  name="customer"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    return (
                      <>
                        <Select
                          name="customer"
                          onChange={(e) => {
                            field.onChange(e || '')
                            setValue('order', null)
                            setValue('workPackage', null)
                          }}
                          className="d-flex flex-column gap-2 w-100"
                          options={customers}
                          value={
                            customers.find(
                              (option: SelectOption) =>
                                option.value === field.value,
                            ) || null
                          }
                          label={t('timeTracking.form.customer')}
                          placeholder={t('timeTracking.form.selectCustomer')}
                          isClearable={false}
                          hasError={!!error}
                          components={tooltipComponents}
                          classNames={clampMenuClassNames}
                        />
                        <FieldErrorMessage error={error} />
                      </>
                    )
                  }}
                />
              </FormFields>
            </FormGroup>

            <FormGroup>
              <FormFields className="gap-1">
                <Controller
                  name="order"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    const selectedOption =
                      orders?.find(
                        (option: SelectOption) => option.value === field.value,
                      ) || null
                    return (
                      <>
                        <Select
                          name="order"
                          onChange={(selectedOption) => {
                            field.onChange(selectedOption || '')
                            setValue('workPackage', null)
                          }}
                          className="d-flex flex-column gap-2 w-100"
                          options={orders}
                          value={selectedOption}
                          label={t('timeTracking.form.order')}
                          placeholder={t('timeTracking.form.selectOrder')}
                          isClearable={false}
                          isDisabled={!getValues().customer}
                          hasError={!!error}
                          components={tooltipComponents}
                          classNames={clampMenuClassNames}
                        />
                        <FieldErrorMessage error={error} />
                      </>
                    )
                  }}
                />
              </FormFields>
            </FormGroup>

            <FormGroup>
              <FormFields className="gap-1">
                <Controller
                  name="workPackage"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    const selectedOption =
                      workPackages?.find(
                        (option: SelectOption) => option.value === field.value,
                      ) || null
                    return (
                      <>
                        <Select
                          name="workPackage"
                          className="d-flex flex-column gap-2 w-100"
                          options={workPackages}
                          label={t('timeTracking.form.workPackage')}
                          placeholder={t('timeTracking.form.selectWorkPackage')}
                          isClearable={false}
                          value={selectedOption}
                          isDisabled={!getValues().order}
                          onChange={(selectedOption) => {
                            field.onChange(selectedOption || '')
                            const pkg = workPackages.find(
                              (p) => p.value === selectedOption,
                            )
                            setValue('workPackagePosition', pkg?.position || '')
                            setValue('workPackageBudget', pkg?.budget)
                          }}
                          hasError={!!error}
                          components={tooltipComponents}
                          classNames={clampMenuClassNames}
                        />
                        <FieldErrorMessage error={error} />
                      </>
                    )
                  }}
                />
              </FormFields>
            </FormGroup>

            <FormHeader>
              <StringText variant="title" className="lh-base fs-6">
                {t('timeTracking.form.task')}
              </StringText>
            </FormHeader>
            <div className="container m-0 p-0 mw-100">
              <div className="row">
                <div className="col-sm-12 col-lg-6">
                  <FormGroup>
                    <FormFields className="gap-1">
                      <Controller
                        name="workDuration"
                        control={control}
                        render={({ field, fieldState: { error } }) => {
                          const { ref: _, onChange, ...restField } = field
                          return (
                            <Input
                              {...restField}
                              value={field.value || ''}
                              name="workDuration"
                              label={t('timeTracking.form.workDuration')}
                              placeholder={t('timeTracking.form.workDuration')}
                              className="d-flex flex-column gap-2 w-100"
                              type="number"
                              step="0.5"
                              min="0"
                              max="24"
                              onWheel={(event) => event.currentTarget.blur()}
                              onChange={(event) => {
                                onChange(event)
                                if (!isLeader) return
                                const { isDirty: billedChanged } =
                                  getFieldState('billedDuration')
                                if (!billedChanged) {
                                  setValue(
                                    'billedDuration',
                                    Number(event.target.value) || 0,
                                  )
                                }
                              }}
                              inputWrapperClassName={classNames({
                                'border-danger': error,
                              })}
                            />
                          )
                        }}
                      />
                      {getFieldState('workDuration').error && (
                        <FieldErrorMessage
                          error={getFieldState('workDuration').error}
                        />
                      )}
                    </FormFields>
                  </FormGroup>
                </div>
                <div className="col-sm-12 col-lg-6">
                  <FormGroup>
                    <FormFields className="gap-1">
                      <Controller
                        name="typeOfActivity"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <>
                            <Select
                              name="typeOfActivity"
                              onChange={(e) => {
                                field.onChange(e || '')
                                setValue('productType', null)
                              }}
                              value={
                                typesOfActivity.find(
                                  (option: SelectOption) =>
                                    option.value === field.value,
                                ) || null
                              }
                              className="d-flex flex-column gap-2 w-100"
                              options={typesOfActivity}
                              label={t('timeTracking.form.typeOfActivity')}
                              placeholder={t(
                                'timeTracking.form.selectTypeOfActivity',
                              )}
                              isClearable={false}
                              hasError={!!error}
                            />
                            <FieldErrorMessage error={error} />
                          </>
                        )}
                      />
                    </FormFields>
                  </FormGroup>
                </div>
              </div>
              <div className="row">
                <div className="col-12 text-muted text-small p-2 fw-normal pb-0">
                  {t('timeTracking.form.workDurationHint')}
                </div>
              </div>
            </div>

            {isLeader && <LogTimeFormBillingSection />}

            {isSupportType &&
              (() => {
                const localizedProductTypes = productsTypes.map((option) => ({
                  ...option,
                  label: t(`timeTracking.productTypes.${option.value}`),
                }))
                return (
                  <FormGroup>
                    <FormFields className="gap-1">
                      <Controller
                        name="productType"
                        control={control}
                        render={({ field, fieldState: { error } }) => (
                          <>
                            <Select
                              name="productType"
                              onChange={(e) => field.onChange(e)}
                              className="d-flex flex-column gap-2 w-100"
                              options={localizedProductTypes}
                              label={t('timeTracking.form.productType')}
                              placeholder={t(
                                'timeTracking.form.selectProductType',
                              )}
                              isClearable={false}
                              value={
                                localizedProductTypes.find(
                                  (option: SelectOption) =>
                                    option.value === field.value,
                                ) || null
                              }
                              hasError={!!error}
                            />
                            <FieldErrorMessage error={error} />
                          </>
                        )}
                      />
                    </FormFields>
                  </FormGroup>
                )
              })()}

            <FormGroup>
              <FormFields className="gap-2">
                <div className="d-flex justify-content-between align-items-center w-100">
                  <StringText className="mb-2" variant="title">
                    {t('timeTracking.form.description')}
                  </StringText>
                  <div className="text-muted fw-normal text-small">{`${descriptionLength}/${DESCRIPTION_MAX_LENGTH}`}</div>
                </div>

                <div className="badge-array-wrapper p-0">
                  {predefinedTasks.map((task: SelectOption, index: number) => {
                    const taskLabel = t(
                      `timeTracking.predefinedTasks.${task.value}`,
                    )
                    return (
                      <TaskButton
                        key={index}
                        label={taskLabel}
                        onClick={() => {
                          setValue('description', taskLabel)
                          triggerValidation('description')
                        }}
                        isToggled={false}
                      />
                    )
                  })}
                </div>
              </FormFields>
            </FormGroup>
            <FormGroup>
              <FormFields className="gap-1">
                <Controller
                  name="description"
                  control={control}
                  render={({ field }) => {
                    const { ref: _, ...restField } = field
                    return (
                      <Textarea
                        {...restField}
                        onChange={(e) => {
                          field.onChange(e)
                          triggerValidation('description')
                        }}
                        name=""
                        placeholder={t(
                          'timeTracking.form.descriptionPlaceholder',
                        )}
                        className="d-flex flex-column w-100"
                        id="description"
                      />
                    )
                  }}
                />
                {descriptionErrors.map((err, index) => {
                  return <FieldErrorMessage key={index} error={err} />
                })}
              </FormFields>
            </FormGroup>

            <FormGroup>
              <FormFields className="gap-1">
                <Controller
                  name="internalNote"
                  control={control}
                  render={({ field, fieldState: { error } }) => {
                    const { ref: _, ...restField } = field
                    return (
                      <>
                        <Textarea
                          {...restField}
                          label={t('timeTracking.form.internalRemarks')}
                          placeholder={t(
                            'timeTracking.form.descriptionPlaceholder',
                          )}
                          className="d-flex flex-column gap-2 w-100"
                          id="internal-remarks"
                          name="internalNote"
                        />
                        <FieldErrorMessage error={error} />
                      </>
                    )
                  }}
                />
              </FormFields>
            </FormGroup>
          </FormBody>
        </fieldset>
      </Form>
      <Tooltip
        id={LABEL_TOOLTIP_ID}
        delayShow={0}
        place="right"
        noArrow={false}
        offset={24}
        className="log-time-select-tooltip"
      />
    </div>
  )
}
